import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import apiClient from '../api/client';
import { ArticleImage } from './ui/ArticleImage';
import { Skeleton } from './ui/Skeleton';
import { specLabel, specValue } from '../lib/format';

/**
 * The identity panel behind a search row's four links.
 *
 * Those links used to be four buttons wired to the SAME handler, all navigating to `/catalog/:id`
 * — so they behaved identically and threw away the sale in progress to do it. A seller comparing
 * equivalents opens these constantly; leaving the page to read a cross-reference and coming back
 * to an empty search is the wrong trade every time.
 *
 * One fetch feeds all four tabs, and the button that was clicked decides which one opens.
 */
export type ArticleInfoTab = 'INFO' | 'CROSS' | 'OE' | 'VEHICLES';

export interface ArticleInfo {
  reference: string;
  designation: string | null;
  brand: string | null;
  imageUrl: string | null;
  brandImageUrl: string | null;
  eans: string[];
  specifications: { label: string; value: string; unit?: string | null }[];
  crossReferences: string[];
  oemReferences: string[];
  vehicles: {
    make: string;
    model: string;
    generation: string | null;
    yearFrom: number | null;
    yearTo: number | null;
    engine: string | null;
    powerHp: number | null;
    powerKw: number | null;
  }[];
  vehicleCount: number;
}

const TABS: ArticleInfoTab[] = ['INFO', 'CROSS', 'OE', 'VEHICLES'];

export function ArticleInfoDialog({
  reference,
  tab,
  onTab,
  onClose,
}: {
  reference: string;
  tab: ArticleInfoTab;
  onTab: (t: ArticleInfoTab) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [info, setInfo] = useState<ArticleInfo | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setInfo(null);
    setFailed(false);
    apiClient
      .get<ArticleInfo>(`/catalog/article-info?ref=${encodeURIComponent(reference)}`)
      .then(({ data }) => alive && setInfo(data))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [reference]);

  // Escape closes, like every other dismissible surface in the app.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('components.article.dialogAria', { reference })}
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-lg border bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start gap-3 border-b p-4">
          <ArticleImage
            src={info?.imageUrl}
            brandSrc={info?.brandImageUrl}
            alt={info?.brand ?? reference}
            size={44}
          />
          <div className="min-w-0 flex-1">
            <div className="font-mono text-sm font-semibold">{reference}</div>
            <div className="truncate text-sm text-muted-foreground">
              {info ? [info.brand, info.designation].filter(Boolean).join(' · ') : '…'}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label={t('components.article.close')}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <nav className="flex gap-1 border-b px-3 pt-2" role="tablist">
          {TABS.map((key) => (
            <button
              key={key}
              role="tab"
              aria-selected={tab === key}
              onClick={() => onTab(key)}
              className={`rounded-t-md px-3 py-1.5 text-sm transition-colors ${
                tab === key
                  ? 'border-b-2 border-primary font-medium text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t(`components.article.tabs.${key}`)}
              {key === 'CROSS' && info ? ` (${info.crossReferences.length})` : ''}
              {key === 'OE' && info ? ` (${info.oemReferences.length})` : ''}
              {key === 'VEHICLES' && info ? ` (${info.vehicleCount})` : ''}
            </button>
          ))}
        </nav>

        <div className="min-h-[220px] overflow-y-auto p-4 text-sm">
          {failed ? (
            <p className="py-8 text-center text-muted-foreground">
              {t('components.article.noInfo')}
            </p>
          ) : !info ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-3/5" />
            </div>
          ) : (
            <Body info={info} tab={tab} />
          )}
        </div>
      </div>
    </div>
  );
}

function Body({ info, tab }: { info: ArticleInfo; tab: ArticleInfoTab }) {
  const { t } = useTranslation();
  if (tab === 'INFO') {
    return (
      <div className="space-y-4">
        {info.specifications.length > 0 ? (
          <table className="w-full">
            <tbody>
              {info.specifications.map((s, i) => (
                <tr key={`${s.label}-${i}`} className="border-t first:border-t-0">
                  <td className="py-1.5 pr-4 text-muted-foreground">{specLabel(s)}</td>
                  <td className="py-1.5 font-medium">{specValue(s)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>{t('components.article.noSpecs')}</Empty>
        )}
        {info.eans.length > 0 && (
          <div>
            <h3 className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">EAN</h3>
            <div className="flex flex-wrap gap-1.5">
              {info.eans.map((e) => (
                <Code key={e}>{e}</Code>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (tab === 'CROSS' || tab === 'OE') {
    const list = tab === 'CROSS' ? info.crossReferences : info.oemReferences;
    if (list.length === 0) {
      return (
        <Empty>
          {tab === 'CROSS' ? t('components.article.noCross') : t('components.article.noOe')}
        </Empty>
      );
    }
    return (
      <div className="flex flex-wrap gap-1.5">
        {list.map((n) => (
          <Code key={n}>{n}</Code>
        ))}
      </div>
    );
  }

  if (info.vehicles.length === 0) {
    return (
      <Empty>
        {info.vehicleCount > 0
          ? t('components.article.fitmentsNotSynced', { count: info.vehicleCount })
          : t('components.article.noVehicles')}
      </Empty>
    );
  }
  // Grouped by make: a filter fitting 166 vehicle types is a wall of text otherwise, and the
  // seller is scanning for one manufacturer.
  const byMake = new Map<string, ArticleInfo['vehicles']>();
  for (const v of info.vehicles) {
    const list = byMake.get(v.make) ?? [];
    list.push(v);
    byMake.set(v.make, list);
  }
  return (
    <div className="space-y-4">
      {info.vehicles.length < info.vehicleCount && (
        <p className="text-xs text-muted-foreground">
          {t('components.article.showingFitments', {
            shown: info.vehicles.length,
            total: info.vehicleCount,
          })}
        </p>
      )}
      {[...byMake.entries()].map(([make, list]) => (
        <section key={make}>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {make} <span className="font-normal">({list.length})</span>
          </h3>
          <ul className="space-y-0.5">
            {list.map((v, i) => (
              <li
                key={`${v.model}-${i}`}
                className="flex flex-wrap gap-x-2 border-t py-1 first:border-t-0"
              >
                <span>{v.model}</span>
                {v.generation && <span className="text-muted-foreground">{v.generation}</span>}
                {v.powerHp != null && (
                  <span className="text-muted-foreground">
                    {v.powerHp} hp{v.powerKw != null ? ` / ${v.powerKw} kW` : ''}
                  </span>
                )}
                {(v.yearFrom || v.yearTo) && (
                  <span className="text-muted-foreground">
                    {v.yearFrom ?? '…'}–{v.yearTo ?? ''}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

const Empty = ({ children }: { children: React.ReactNode }) => (
  <p className="py-8 text-center text-muted-foreground">{children}</p>
);
const Code = ({ children }: { children: React.ReactNode }) => (
  <span className="rounded border bg-muted/40 px-1.5 py-0.5 font-mono text-xs">{children}</span>
);
